import React, { useEffect, useState } from "react";
import { API_BASE } from "../config";
import { authHeaders } from "../utils/authHeaders";

export default function AdminUsers() {
  const [admins, setAdmins] = useState([]);
  const [fpos, setFpos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "fpo_admin",
    fpo_id: "",
  });

  const loadFpos = async () => {
    const res = await fetch(`${API_BASE}/fpos`, {
      headers: authHeaders(),
    });

    const data = await res.json();

    if (!res.ok || data.success === false) {
      throw new Error(data.message || "Failed to load FPOs");
    }

    const list = Array.isArray(data.data) ? data.data : [];
    setFpos(list);

    if (!form.fpo_id && list.length > 0) {
      setForm((prev) => ({
        ...prev,
        fpo_id: String(list[0].id),
      }));
    }
  };

  const loadAdmins = async () => {
    const res = await fetch(`${API_BASE}/admin-users`, {
      headers: authHeaders(),
    });

    const data = await res.json();

    if (!res.ok || data.success === false) {
      throw new Error(data.message || "Failed to load admin users");
    }

    setAdmins(Array.isArray(data.data) ? data.data : []);
  };

  const loadAll = async () => {
    setLoading(true);
    setMessage("");

    try {
      await loadFpos();
      await loadAdmins();
    } catch (err) {
      console.error("Load admin users error:", err);
      setMessage(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      setMessage("Name, email and password are required");
      return;
    }

    if (form.role === "fpo_admin" && !form.fpo_id) {
      setMessage("Please select FPO for FPO Admin");
      return;
    }

    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        fpo_id: Number(form.fpo_id || 1),
      };

      const res = await fetch(`${API_BASE}/admin-users`, {
        method: "POST",
        headers: authHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to create admin user");
      }

      setMessage("Admin user created successfully");

      setForm({
        name: "",
        email: "",
        password: "",
        role: "fpo_admin",
        fpo_id: fpos.length > 0 ? String(fpos[0].id) : "",
      });

      await loadAdmins();
    } catch (err) {
      console.error("Create admin user error:", err);
      setMessage(err.message || "Failed to create admin user");
    }
  };

  const handleResetPassword = async (admin) => {
    const newPassword = window.prompt(
      `Enter new password for ${admin.name} (${admin.email})`
    );

    if (!newPassword) return;

    if (newPassword.length < 4) {
      setMessage("New password must be at least 4 characters");
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/admin-users/${admin.id}/reset-password`,
        {
          method: "POST",
          headers: authHeaders({
            "Content-Type": "application/json",
          }),
          body: JSON.stringify({
            new_password: newPassword,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to reset password");
      }

      setMessage(`Password reset successfully for ${admin.name}`);
    } catch (err) {
      console.error("Reset admin password error:", err);
      setMessage(err.message || "Failed to reset password");
    }
  };

  return (
    <div>
      <h2>FPO Admin Users</h2>

      <p style={{ color: "#555" }}>
        Super Admin can create FPO Admin users and assign them to an FPO.
      </p>

      {message && (
        <div
          style={{
            marginBottom: 16,
            padding: 10,
            background: "#f5f5f5",
            border: "1px solid #ddd",
          }}
        >
          {message}
        </div>
      )}

      <form
        onSubmit={handleCreate}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(140px, 1fr)) auto",
          gap: 10,
          alignItems: "end",
          marginBottom: 24,
        }}
      >
        <div>
          <label>Name *</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Admin Name"
            style={{ width: "100%", padding: 8 }}
          />
        </div>

        <div>
          <label>Email *</label>
          <input
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Email"
            style={{ width: "100%", padding: 8 }}
          />
        </div>

        <div>
          <label>Password *</label>
          <input
            name="password"
            type="password"
            value={form.password}
            onChange={handleChange}
            placeholder="Password"
            style={{ width: "100%", padding: 8 }}
          />
        </div>

        <div>
          <label>Role</label>
          <select
            name="role"
            value={form.role}
            onChange={handleChange}
            style={{ width: "100%", padding: 8 }}
          >
            <option value="fpo_admin">FPO Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>

        <div>
          <label>FPO</label>
          <select
            name="fpo_id"
            value={form.fpo_id}
            onChange={handleChange}
            style={{ width: "100%", padding: 8 }}
          >
            {fpos.map((fpo) => (
              <option key={fpo.id} value={fpo.id}>
                {fpo.name}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          style={{
            padding: "9px 14px",
            background: "#1976D2",
            color: "#fff",
            border: "none",
            cursor: "pointer",
          }}
        >
          Create
        </button>
      </form>

      <h3>Admin User List</h3>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table
          border="1"
          cellPadding="8"
          cellSpacing="0"
          style={{ borderCollapse: "collapse", width: "100%" }}
        >
          <thead>
            <tr style={{ background: "#f0f0f0" }}>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>FPO</th>
              <th>Created At</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {admins.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center" }}>
                  No admin user found
                </td>
              </tr>
            ) : (
              admins.map((admin) => (
                <tr key={admin.id}>
                  <td>{admin.id}</td>
                  <td>{admin.name}</td>
                  <td>{admin.email}</td>
                  <td>{admin.role}</td>
                  <td>
                    {admin.fpo_name
                      ? `${admin.fpo_name} (#${admin.fpo_id})`
                      : `FPO #${admin.fpo_id}`}
                  </td>
                  <td>
                    {admin.created_at
                      ? new Date(admin.created_at).toLocaleString()
                      : "-"}
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => handleResetPassword(admin)}
                      style={{
                        padding: "6px 10px",
                        background: "#f97316",
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        cursor: "pointer",
                      }}
                    >
                      Reset Password
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}